import React from 'react'

import DnTable from 'src/components/Core/DnTable'

import PackageNameCell from './PackageNameCell'
import ProviderCell from './ProviderCell'
import StatusCell from './StatusCell'

type PackageOrder = {
  packageOrderId: string
  createdAt?: string
  completedDate?: string
  status?: string
  orderType: string
  providerOrderId?: string
  package?: { name?: string }
  providerResponse?: unknown
}

// Widths are percentages and must sum to 100. The longest order-status
// labels ("Pending Applicant", "Sent To Requestor") are ~119px. 19% keeps
// them on one line inside the cell at common laptop widths. StatusCell wraps
// if the column is narrower. Dates stay at 18% so "10/21/2026, 07:04 AM"
// still fits. The extra status width comes from Provider, Package Name, and
// Order ID.
export const ORDER_TABLE_COLUMN_WIDTHS = {
  provider: 12,
  packageName: 17,
  providerOrderId: 16,
  status: 19,
  createdAt: 18,
  completedDate: 18,
} as const

type Props = {
  orders: PackageOrder[]
  loading: boolean
  loadingEnhancedReportId?: string | null
  onEnhancedReportClick: (packageOrderId: string) => void
  onRowClick?: (order: PackageOrder) => void
  totalCount?: number
  onLoadPage?: (pageInfo: { offset: number; limit: number }) => void
}

const OrdersTable = ({
  loading,
  orders,
  loadingEnhancedReportId,
  onEnhancedReportClick,
  onRowClick,
  totalCount,
  onLoadPage,
}: Props) => {
  const columns = [
    {
      key: 'provider',
      width: ORDER_TABLE_COLUMN_WIDTHS.provider,
      label: 'Provider',
      render: (order: PackageOrder) => (
        <ProviderCell
          key={`provider-${order.packageOrderId}`}
          orderType={order.orderType}
          providerOrderId={order.providerOrderId}
          isLoadingEnhancedReport={
            loadingEnhancedReportId === order.packageOrderId
          }
          onEnhancedReportClick={() =>
            onEnhancedReportClick(order.packageOrderId)
          }
        />
      ),
    },
    {
      key: 'packageName',
      width: ORDER_TABLE_COLUMN_WIDTHS.packageName,
      label: 'Package Name',
      render: (order: PackageOrder) => (
        <PackageNameCell
          key={`pkg-${order.packageOrderId}`}
          packageName={order.package?.name}
          packageOrderId={order.packageOrderId}
        />
      ),
    },
    {
      key: 'providerOrderId',
      width: ORDER_TABLE_COLUMN_WIDTHS.providerOrderId,
      label: 'Order ID',
      render: (order: PackageOrder) =>
        order.providerOrderId && order.providerOrderId !== 'undefined'
          ? order.providerOrderId
          : '',
    },
    {
      key: 'status',
      width: ORDER_TABLE_COLUMN_WIDTHS.status,
      // Resize floor. Below this, StatusCell wraps instead of overlapping.
      minWidth: 16,
      label: 'Order Status',
      render: (order: PackageOrder) => (
        <StatusCell
          key={`status-${order.packageOrderId}`}
          status={order.status}
        />
      ),
    },
    {
      key: 'createdAt',
      width: ORDER_TABLE_COLUMN_WIDTHS.createdAt,
      label: 'Created Date',
      render: (order: PackageOrder) =>
        order.createdAt
          ? new Date(order.createdAt).toLocaleString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '',
    },
    {
      key: 'completedDate',
      width: ORDER_TABLE_COLUMN_WIDTHS.completedDate,
      label: 'Completed Date',
      render: (order: PackageOrder) =>
        order.completedDate
          ? new Date(order.completedDate).toLocaleString('en-US', {
              month: '2-digit',
              day: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '',
    },
  ]

  return (
    <DnTable
      enableColumnResize={true}
      columns={columns}
      loading={loading}
      data={orders}
      onRowClick={(idx) => onRowClick?.(orders[idx])}
      totalCount={totalCount}
      onLoadPage={onLoadPage}
    />
  )
}

export default OrdersTable
